import { useId, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { textoObrigatorio, textoOpcional } from '@malgoos/shared';
import { Botao } from '../../componentes/base';
import { AreaSensivel, EntradaSensivel, Rotulo } from '../../componentes/formulario';
import { invalidarFichasDoParceiro, AvisoProfissionalDesativado, CamposReuniaoLivre, edicaoLivreParaApi, estadoLivreDaReuniao, estadoLivreNovo, horarioLivreParaApi, reuniaoLivreParaApi, temErroForaDaTela, usePreparoLivre, validarReuniaoLivre } from '../../componentes/reuniao-livre';
import { Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { ariaErro, useValidacaoLocal } from '../../componentes/validacao-local';
import { ErroRequisicao, mensagemDeErro, patch, post } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { CHAVE } from './dados';
import { ESTADOS_ABERTOS, type Agendamento, type EventoGoogleDados } from './tipos';

export { CamposReuniaoLivre } from '../../componentes/reuniao-livre';

/** Campos que o formulário mostra; erro em outro campo do servidor sobe para a mensagem do topo. */
const CAMPOS_NA_TELA = ['assunto', 'observacoes', 'dia', 'dia_fim', 'inicio', 'fim', 'ate', 'recorrencia', 'local', 'convidados', 'reuniao_tipo'];
const MAX_DESCRICAO = 2000;

/**
 * Reunião livre (ficha 2026-11-03): o formulário igual ao do Google Agenda, para criar e editar. Nada é obrigatório além de
 * título e horário; conflito só avisa. `parceiroId` e `oportunidadeId` levam o vínculo de quem abriu de dentro de um contato,
 * empresa ou negociação, sem campo a preencher. Montado só com a janela aberta: fechar e reabrir começa limpo.
 */
export function FormReuniaoLivre({ atual, doGoogle, inicio, parceiroId, oportunidadeId, aoFechar, aoSalvar }: { atual?: Agendamento | null; doGoogle?: { bloqueioId: string; dados: EventoGoogleDados } | null; inicio?: Date | null; parceiroId?: string | null; oportunidadeId?: string | null; aoFechar: () => void; aoSalvar?: () => void }) {
  const { t } = useTranslation('agenda');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const id = useId();
  const [novoId] = useState(() => crypto.randomUUID());
  const origem = atual ?? doGoogle?.dados;
  const [titulo, setTitulo] = useState(origem?.assunto ?? '');
  const [descricao, setDescricao] = useState(origem?.observacoes ?? '');
  const [inicial] = useState(() => (atual ? estadoLivreDaReuniao(atual, s.workspace.fuso) : doGoogle ? estadoLivreDaReuniao(doGoogle.dados, s.workspace.fuso) : estadoLivreNovo(inicio ?? null)));
  const [estado, setEstado] = useState(inicial);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  useValidacaoLocal(erros, erro);

  // O preparo e a consulta de conflitos falam de quem pede: na reunião de outra pessoa não dizem nada sobre ela.
  const minha = !atual || atual.responsavel_usuario_id === s.usuario.id;
  const preparo = usePreparoLivre(minha);
  const editando = !!atual || !!doGoogle;
  const desativado = !editando && preparo.data?.profissional_desativado === true;
  // Val5 (29/10): mudar o horário é remarcar (`agenda.cancelar`), e reunião encerrada não remarca (rlote-11).
  const encerrada = !!atual && !ESTADOS_ABERTOS.includes(atual.estado);
  const travaHorario = !!atual && (encerrada || !s.pode('agenda.cancelar'));
  const nomes = [atual?.parceiro_nome, ...estado.convidados.map((c) => c.nome)];

  const recarregar = async () => {
    await qc.invalidateQueries({ queryKey: [CHAVE] });
    await qc.invalidateQueries({ queryKey: ['/agenda/agendamentos'] });
    if (atual) await qc.invalidateQueries({ queryKey: ['chatter', 'agenda.agendamento', atual.id] });
    if (parceiroId) await invalidarFichasDoParceiro(qc, parceiroId);
    if (oportunidadeId) {
      await qc.invalidateQueries({ queryKey: ['pipeline'] });
      await qc.invalidateQueries({ queryKey: ['chatter', 'pipeline.oportunidade', oportunidadeId] });
    }
  };

  const salvar = async () => {
    const locais = validarReuniaoLivre(estado, tn('erros.obrigatorio'), t);
    const assunto = textoObrigatorio('assunto', titulo, 200);
    if (!assunto.ok) locais.assunto = tn('erros.obrigatorio');
    setErro(null);
    setErros(locais);
    if (Object.keys(locais).length || !assunto.ok) return;
    const obs = textoOpcional('observacoes', descricao, MAX_DESCRICAO);
    const observacoes = obs.ok ? obs.valor : descricao;
    setOcupado(true);
    let gravou = false;
    try {
      if (atual) {
        await patch(`/agenda/agendamentos/${atual.id}`, { assunto: assunto.valor, observacoes, ...edicaoLivreParaApi(estado, atual) });
        gravou = true;
        // Rodada rlote-4 (ROB-M1): só remarca quando a pessoa mudou o horário.
        const horario = horarioLivreParaApi(estado);
        if (!travaHorario && JSON.stringify(horario) !== JSON.stringify(horarioLivreParaApi(inicial))) await post(`/agenda/agendamentos/${atual.id}/remarcar`, horario);
      } else if (doGoogle) {
        await post(`/agenda/bloqueios/${doGoogle.bloqueioId}/evento-google`, { id: novoId, versao_google: doGoogle.dados.versao_google, assunto: assunto.valor, observacoes, ...reuniaoLivreParaApi(estado) });
      } else {
        await post('/agenda/reunioes', { id: novoId, assunto: assunto.valor, observacoes, ...reuniaoLivreParaApi(estado), ...(parceiroId ? { parceiro_id: parceiroId } : {}), ...(oportunidadeId ? { oportunidade_id: oportunidadeId } : {}) });
      }
      await recarregar();
      const meetNovo = estado.meet && origem?.reuniao_tipo !== 'google_meet';
      toast.sucesso(meetNovo ? t('reuniaoLivre.salvaComMeet') : t(editando ? 'crm.atualizada' : 'crm.agendada'));
      aoSalvar?.();
      aoFechar();
    } catch (e) {
      if (gravou) await recarregar();
      const porCampo = e instanceof ErroRequisicao ? e.porCampo() : {};
      setErros(porCampo);
      if (temErroForaDaTela(porCampo, CAMPOS_NA_TELA)) setErro(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  };

  const idDesativado = `${id}-desativado`;
  const idAvisoGoogle = `${id}-aviso-google`;
  // Rodada rge-2 (VIS-M2): o que se perde ao salvar também chega a quem usa leitor de tela, no próprio botão.
  const temAvisoGoogle = !!doGoogle && (doGoogle.dados.repetido || doGoogle.dados.descricao_cortada || doGoogle.dados.descricao_formatada || doGoogle.dados.outro_corte);
  return (
    <Modal
      aberto
      largura="lg"
      aoFechar={aoFechar}
      titulo={editando ? t('crm.editar') : t('crm.agendar')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={ocupado} disabled={desativado} aria-describedby={desativado ? idDesativado : temAvisoGoogle ? idAvisoGoogle : undefined} onClick={() => void salvar()} data-salvar-reuniao="">
            {editando ? tn('comum.salvar') : t('crm.agendar')}
          </Botao>
        </>
      }
    >
      <div className="flex min-w-0 flex-col gap-4" data-form-reuniao-livre="">
        {desativado && <AvisoProfissionalDesativado id={idDesativado} />}
        {doGoogle && temAvisoGoogle && (
          <div id={idAvisoGoogle} className="rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm" data-aviso-evento-google="">
            {doGoogle.dados.repetido && <p>{t('eventoGoogle.repetido')}</p>}
            {doGoogle.dados.descricao_cortada && <p>{t('eventoGoogle.cortada', { max: MAX_DESCRICAO })}</p>}
            {doGoogle.dados.descricao_formatada && <p>{t('eventoGoogle.formatada')}</p>}
            {doGoogle.dados.outro_corte && <p>{t('eventoGoogle.outroCorte')}</p>}
          </div>
        )}
        {erro && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
            {erro}
          </p>
        )}
        <Rotulo id={`${id}-titulo`} rotulo={t('reuniaoLivre.titulo')} obrigatorio erro={erros.assunto}>
          <EntradaSensivel mascara={null} livre nomesNoTexto={nomes} id={`${id}-titulo`} {...ariaErro(`${id}-titulo`, erros.assunto)} name="titulo" value={titulo} maxLength={200} placeholder={t('reuniaoLivre.tituloAjuda')} onChange={(e) => setTitulo(e.target.value)} />
        </Rotulo>
        <CamposReuniaoLivre
          idBase={id}
          estado={estado}
          aoMudar={setEstado}
          erros={erros}
          preparo={preparo}
          nomes={nomes}
          semRepeticao={editando}
          travaHorario={travaHorario}
          ajudaTravado={t(encerrada ? 'reuniaoLivre.horarioEncerrada' : 'reuniaoLivre.horarioSemPermissao')}
          ignorarId={atual?.id ?? doGoogle?.bloqueioId}
          avisarConflitos={minha}
          // Rodada rrl-1: na reunião de outra pessoa, a mudança sai pelo e-mail do sistema, mesmo com a conta dela conectada.
          conviteGoogle={atual ? !!atual.convite_google && minha : doGoogle ? true : undefined}
          servidorDecideMeet={!!atual && (!minha || !s.pode('agenda.criar'))}
        />
        <Rotulo id={`${id}-descricao`} rotulo={t('reuniaoLivre.descricao')} erro={erros.observacoes} ajuda={`${t('reuniaoLivre.descricaoAjuda')} ${descricao.length}/${MAX_DESCRICAO}`}>
          <AreaSensivel nomesNoTexto={nomes} rotulo={t('reuniaoLivre.descricao')} id={`${id}-descricao`} {...ariaErro(`${id}-descricao`, erros.observacoes)} aria-describedby={erros.observacoes ? `${id}-descricao-erro` : `${id}-descricao-ajuda`} name="descricao" rows={4} maxLength={MAX_DESCRICAO} className="w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-base text-tinta-1 placeholder:text-tinta-3 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco md:text-sm" value={descricao} onChange={(e) => setDescricao(e.target.value)} />
        </Rotulo>
      </div>
    </Modal>
  );
}
